<script>
import Layout from "@/router/layouts/main";
import PageHeader from "@/components/page-header";
import { required } from "vuelidate/lib/validators";
import User from "@/services/westore/entities/user";
import appConfig from "@/app.config.json";
import { mapMutations, mapGetters } from "vuex";
import FormSkeleton from "../../../components/skeletons/form-skeleton.vue";

const base64Encode = (data) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(data);
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.onerror = (error) => reject(error);
  });

export default {
  page: {
    title: "Profile",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  name: "profile",
  components: { Layout, PageHeader, FormSkeleton },
  data() {
    return {
      title: "Profile",
      breadcrumb: [
        {
          text: "Home",
          href: "/",
        },
        {
          text: "Profile",
          active: true,
        },
      ],
      submitted: false,
      loader: false,
      updated: false,
      hasError: false,
      errorMessage: "",
      tradeLicense: null,
      otherDocument: null,
      tradeLicenseSrc: null,
      otherDocumentSrc: null,
      user: null,
      initialUser: null,
      showContentLoader: true,
      files: {
        tradeLicense: null,
        otherDocument: null,
      },
      languages: [
        { name: "English", code: "en" },
        { name: "Arabic", code: "ar" },
      ],
      orderCancellation: [
        { name: "ON", value: 1 },
        { name: "OFF", value: 0 },
      ],
    };
  },
  computed: {
    ...mapGetters({
      selectedLang: ["layout/lang"],
    }),
  },
  validations: {
    user: {
      email: { required },
      phone: { required },
    },
  },
  created() {
    this.fetchUser().then((user) => {
      this.user = user;
      this.initialUser = Object.assign({}, user);
      this.showContentLoader = false;
    });
  },

  methods: {
    ...mapMutations({
      setUser: "auth/SET_USER",
      setMeta: "auth/SET_META",
      changeLang: "layout/CHANGE_LANG",
    }),
    fetchUser() {
      return User.info().then(({ data: user }) => {
        return user;
      });
    },
    updateUser() {
      this.$v.$touch();
      if (this.$v.$invalid) return;

      const data = new FormData();
      let hasFiles = false;
      let hasData = false;
      Object.keys(this.user).forEach((key) => {
        if (this.user[key] !== this.initialUser[key]) {
          if (!hasData) {
            hasData = true;
          }
          data.append(key, this.user[key]);
        }
      });

      Object.keys(this.files).forEach((key) => {
        if (this.files[key] !== null) {
          if (!hasFiles) {
            hasFiles = true;
          }
          data.append(key, this.files[key]);
        }
      });

      if (!hasData && !hasFiles) return;

      if (hasFiles) {
        User.hasFiles();
      }
      this.submitted = true;
      this.loader = true;
      User.update(data)
        .then(({ data: user, meta }) => {
          this.user = user;
          this.initialUser = Object.assign({}, user);
          this.clearImageResources();
          this.updated = true;
          return { user, meta };
        })
        .then(({ user, meta }) => {
          this.setUser(Object.assign({}, user));
          this.setMeta(meta);
          location.reload();
        })
        .catch((err) => {
          this.errorMessage = "Error! try again later";
          if ("response" in err) {
            if (err.response.status === 422) {
              this.errorMessage = "";
              const errors = err.response.data;
              if ("message" in errors) {
                this.errorMessage = errors.message;
              } else {
                Object.values(errors).forEach((validations) => {
                  validations.forEach((validation) => {
                    this.errorMessage += `${validation}<br>`;
                  });
                });
              }
            } else if (
              err.response.status !== 500 ||
              ("data" in err.response.data &&
                "message" in err.response.data.data)
            ) {
              this.errorMessage = err.response.data.data.message;
            }
          }

          this.hasError = true;
        })
        .finally(() => {
          this.submitted = false;
        });
    },
    isImage(value) {
      if (value === null) {
        return false;
      }
      const isImage = new RegExp("^image");
      const type = value.split(";")[0].split(":")[1];
      return isImage.test(type);
    },
    isFile(value) {
      if (value === null) {
        return false;
      }
      const isFile = new RegExp("pdf|doc|docx$");
      const type = value.split(";")[0].split(":")[1];
      return isFile.test(type);
    },
    clearImageResources() {
      this.tradeLicense = null;
      this.tradeLicenseSrc = null;
      this.files.trade_license = null;

      this.otherDocument = null;
      this.otherDocumentSrc = null;
      this.files.other_documents = null;
    },
    setLanguage(locale, country, flag) {
      this.lan = locale;
      this.text = country;
      this.flag = flag;
    },
  },
  watch: {
    tradeLicense(newValue, oldValue) {
      if (newValue !== oldValue) {
        if (newValue) {
          this.files.trade_license = newValue;
          base64Encode(newValue)
            .then((value) => {
              this.tradeLicenseSrc = value;
            })
            .catch(() => {
              this.tradeLicenseSrc = this.files.trade_license = null;
            });
        } else {
          this.tradeLicenseSrc = this.files.trade_license = null;
        }
      }
    },
    otherDocument(newValue, oldValue) {
      if (newValue !== oldValue) {
        if (newValue) {
          this.files.other_documents = newValue;
          base64Encode(newValue)
            .then((value) => {
              this.otherDocumentSrc = value;
            })
            .catch(() => {
              this.otherDocumentSrc = this.files.other_documents = null;
            });
        } else {
          this.otherDocumentSrc = this.files.other_documents = null;
        }
      }
    },
  },
};
</script>

<template>
  <div>
    <div v-if="loader" class="test bg-white d-flex justify-center align-center">
      <div>
        <v-progress-circular
          :size="50"
          color="primary"
          indeterminate
        ></v-progress-circular>
      </div>
    </div>
    <Layout v-else>
      <PageHeader :title="title" :items="breadcrumb" />
      <div class="row justify-center">
        <div class="col-sm-12 col-md-10">
          <div class="card">
            <FormSkeleton v-if="showContentLoader" />

            <div v-else class="card-body elevation-0">
              <div class="row">
                <div class="col-12">
                  <b-form @submit.prevent="updateUser">
                    <!-- Email -->

                    <b-form-group
                      class="mb-3"
                      type="email"
                      :label="$t('Email')"
                      label-size="lg"
                      label-for="emailInput"
                    >
                      <b-form-input
                        v-model="user.email"
                        id="emailInput"
                        readonly
                        :class="{
                          'is-invalid': submitted && $v.user.email.$error,
                        }"
                      ></b-form-input>
                    </b-form-group>
                    <!-- Phone -->
                    <b-form-group
                      class="mb-3"
                      type="text"
                      :label="$t('Phone')"
                      label-size="lg"
                      label-for="phoneInput"
                      disabled
                    >
                      <b-form-input
                        v-model="user.phone"
                        id="phoneInput"
                        :class="{
                          'is-invalid': submitted && $v.user.phone.$error,
                        }"
                      ></b-form-input>
                    </b-form-group>
                    <!-- Business Link -->
                    <b-form-group
                      class="mb-3"
                      type="text"
                      :label="$t('Business Link')"
                      label-size="lg"
                      label-for="businessLinkInput"
                      disabled
                    >
                      <b-form-input
                        v-model="user.business_link"
                        id="businessLinkInput"
                      ></b-form-input>
                    </b-form-group>
                    <!-- Company Name -->
                    <b-form-group
                      class="mb-3"
                      type="text"
                      :label="$t('Company Name')"
                      label-size="lg"
                      label-for="companyNameInput"
                      disabled
                    >
                      <b-form-input
                        v-model="user.company_name"
                        id="companyNameInput"
                      ></b-form-input>
                    </b-form-group>
                    <b-form-group
                      class="mb-3"
                      label-size="lg"
                      :label="$t('Language')"
                    >
                      <b-form-select
                        v-model="user.locale"
                        class="form-select"
                        value-field="code"
                        text-field="name"
                        :options="languages"
                        type="text"
                        :class="{
                          'is-invalid':
                            submitted && $v.formInputs.city_id.$error,
                        }"
                      ></b-form-select>
                    </b-form-group>
                    <b-form-group
                      class="mb-3"
                      label-size="lg"
                      :label="$t('Cancel Automation')"
                    >
                      <b-form-select
                        v-model="user.order_cancellation_sync"
                        class="form-select"
                        value-field="value"
                        text-field="name"
                        :options="orderCancellation"
                        type="text"
                        :class="{
                          'is-invalid':
                            submitted &&
                            $v.formInputs.order_cancellation_sync.$error,
                        }"
                      ></b-form-select>
                    </b-form-group>
                    <!-- Trade License -->
                    <b-form-group
                      class="mb-3 form-label"
                      :label="$t('Trade License')"
                      label-size="lg"
                      label-for="tradeLicenseInput"
                    >
                      <div class="doc-file">
                        <div class="preview" v-if="tradeLicense">
                          <b-img
                            v-if="isImage(tradeLicenseSrc)"
                            :src="tradeLicenseSrc"
                            height="100px"
                          ></b-img>
                          <b-embed
                            v-if="isFile(tradeLicenseSrc)"
                            :src="tradeLicenseSrc"
                            height="150px"
                          />
                        </div>
                        <b-form-file
                          v-model="tradeLicense"
                          id="tradeLicenseInput"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          size="lg"
                          accept=".jpg, .jpeg, .png, .pdf, .doc, .docx"
                        ></b-form-file>
                      </div>
                    </b-form-group>
                    <!-- Other Document -->
                    <b-form-group
                      class="mb-3 form-label"
                      :label="$t('Other Document')"
                      label-size="lg"
                      label-for="otherDocumentInput"
                    >
                      <div class="doc-file">
                        <div class="preview" v-if="otherDocument">
                          <b-img
                            v-if="isImage(otherDocumentSrc)"
                            :src="otherDocumentSrc"
                            height="100px"
                          ></b-img>
                          <b-embed
                            v-if="isFile(otherDocumentSrc)"
                            :src="otherDocumentSrc"
                            height="150px"
                          />
                        </div>
                        <b-form-file
                          v-model="otherDocument"
                          id="otherDocumentInput"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          size="lg"
                          accept=".jpg, .jpeg, .png, .pdf, .doc, .docx"
                        ></b-form-file>
                      </div>
                    </b-form-group>
                    <div class="row float-end">
                      <div class="col-md-2">
                        <v-progress-circular
                          v-if="loader"
                          :size="30"
                          color="secondary"
                          indeterminate
                        ></v-progress-circular>
                        <b-button
                          v-else
                          type="submit"
                          variant="primary"
                          class="btn-block"
                        >
                          {{ $t("Update") }}
                        </b-button>
                      </div>
                    </div>
                  </b-form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <v-snackbar
        color="red accent-2"
        elavation="24"
        top
        right
        v-model="hasError"
      >
        <div v-html="errorMessage"></div>
      </v-snackbar>
      <v-snackbar color="success" elevation="24" top right v-model="updated">
        Data has been updated successfully.
      </v-snackbar>
    </Layout>
  </div>
</template>

<style lang="scss" scoped>
.doc-file {
  border-radius: 4px;
  border: 1px dashed #000;
  padding: 10px;

  .custom-file-input:lang(en) ~ .custom-file-label::after {
    content: "Browse";
  }

  .preview {
    display: inline-block;
    border-radius: 5px;
    border: 1px solid #1f2326;
    margin: 3px;
  }
}

.doc-file.is-invalid {
  border-color: #f46a6a;
}
.test {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 5000;
  height: 100vh;
  width: 100vw;
}
</style>
